import { useCallback, useEffect, useState } from "react";

/**
 * State backed by localStorage, for preferences rather than data.
 *
 * Every read and write is wrapped: storage throws in private mode and in
 * embedded webviews, and a remembered UI preference is never worth breaking a
 * page over. A failed read simply falls back to `defaultValue`.
 */
export function usePersistentState<T>(
  storageKey: string,
  defaultValue: T,
  isValid: (value: unknown) => value is T,
): [T, (value: T | ((current: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw === null) return defaultValue;
      const parsed: unknown = JSON.parse(raw);
      return isValid(parsed) ? parsed : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(value));
    } catch {
      // Preference only — a storage failure must not surface to the user.
    }
  }, [storageKey, value]);

  const update = useCallback((next: T | ((current: T) => T)) => {
    setValue((current) =>
      typeof next === "function" ? (next as (c: T) => T)(current) : next,
    );
  }, []);

  return [value, update];
}

export const isBoolean = (value: unknown): value is boolean =>
  typeof value === "boolean";

export const isString = (value: unknown): value is string =>
  typeof value === "string";

/**
 * Open/closed state for a disclosure, remembered across reloads.
 *
 * Operators keep the same sections open all day; re-collapsing them after every
 * hard refresh is pure friction. Pass a stable `storageKey` — one that does not
 * include a record id, so the preference is "I always want finance expanded"
 * rather than "expanded for quote 4821".
 */
export function useCollapsible(storageKey: string, defaultOpen = false) {
  const [open, setOpen] = usePersistentState(
    `ir-admin-open:${storageKey}`,
    defaultOpen,
    isBoolean,
  );

  return {
    open,
    toggle: () => setOpen((current) => !current),
    setOpen,
  };
}
